import { type ElementType, type HTMLAttributes, type ReactNode } from 'react';
import classnames from 'src/util/classnames';

interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  fluid?: boolean | string;
  tag?: ElementType;
  children?: ReactNode;
}

export function Container({ fluid, tag: Tag = 'div', className, children, ...props }: ContainerProps) {
  const containerClass =
    fluid === true ? 'container-fluid'
    : typeof fluid === 'string' ? `container-${fluid}`
    : 'container';
  return (
    <Tag className={classnames(containerClass, className)} {...props}>
      {children}
    </Tag>
  );
}

interface RowProps extends HTMLAttributes<HTMLDivElement> {
  noGutters?: boolean;
  form?: boolean;
  xs?: number | string;
  sm?: number | string;
  md?: number | string;
  lg?: number | string;
  xl?: number | string;
  xxl?: number | string;
  children?: ReactNode;
}

export function Row({ noGutters, form, xs, sm, md, lg, xl, xxl, className, children, ...props }: RowProps) {
  return (
    <div
      className={classnames(
        'row',
        noGutters && 'g-0',
        xs && `row-cols-${xs}`,
        sm && `row-cols-sm-${sm}`,
        md && `row-cols-md-${md}`,
        lg && `row-cols-lg-${lg}`,
        xl && `row-cols-xl-${xl}`,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

type ColSize = boolean | number | string | { size?: number | string; offset?: number | string; order?: number | string };

interface ColProps extends HTMLAttributes<HTMLDivElement> {
  xs?: ColSize;
  sm?: ColSize;
  md?: ColSize;
  lg?: ColSize;
  xl?: ColSize;
  xxl?: ColSize;
  children?: ReactNode;
}

function colClass(bp: string, val: ColSize): string[] {
  if (!val && val !== 0) return [];
  if (val === true) return [bp === 'xs' ? 'col' : `col-${bp}`];
  if (typeof val === 'number' || typeof val === 'string') return [bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`];
  const classes: string[] = [];
  if (val.size != null) classes.push(bp === 'xs' ? `col-${val.size}` : `col-${bp}-${val.size}`);
  if (val.offset != null) classes.push(`offset-${bp !== 'xs' ? `${bp}-` : ''}${val.offset}`);
  return classes;
}

export function Col({ xs, sm, md, lg, xl, xxl, className, children, ...props }: ColProps) {
  const hasBreakpoint = xs != null || sm != null || md != null || lg != null || xl != null || xxl != null;
  return (
    <div
      className={classnames(
        !hasBreakpoint && 'col',
        ...colClass('xs', xs ?? false),
        ...colClass('sm', sm ?? false),
        ...colClass('md', md ?? false),
        ...colClass('lg', lg ?? false),
        ...colClass('xl', xl ?? false),
        ...colClass('xxl', xxl ?? false),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
